/**
 * Una renta cobrada FUERA del banco: en efectivo, por Bizum a otra cuenta…
 *
 * No aparece en el extracto, y sin registrarla el inquilino sale como que no ha pagado. Se
 * apunta contra su renta del mes con el justificante —el recibo firmado, la captura del
 * Bizum—, que se descarga después con el resto de papeles del mes. El justificante es
 * opcional: un pago en efectivo sin recibo también es un pago, y se ve en el resumen que falta.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LedgerGap } from '@naar/shared';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Drawer } from '../../components/overlay/Drawer';
import { Field, FormRow, FormSection, Input, MoneyInput, RadioGroup } from '../../components/form/Field';
import { toast } from '../../components/feedback/Toaster';
import { FileDrop } from './FileDrop';
import { avisarReapertura } from './MissingPanel';
import styles from './Ledger.module.css';

interface OffBankPaymentDrawerProps {
  month: string;
  gap: LedgerGap;
  propertyName: string;
  file: File | null;
  onClose: () => void;
}

/** Hoy si cae dentro del mes; si no, el último día del mes, que es cuando se cuadra. */
function fechaPorDefecto(month: string): string {
  const hoy = new Date();
  const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  if (hoyIso.startsWith(month)) return hoyIso;
  const ultimo = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  return `${month}-${String(ultimo).padStart(2, '0')}`;
}

export function OffBankPaymentDrawer({ month, gap, propertyName, file, onClose }: OffBankPaymentDrawerProps) {
  const queryClient = useQueryClient();
  const [metodo, setMetodo] = useState('efectivo');
  const [importe, setImporte] = useState(gap.expectedAmount ?? '');
  const [fecha, setFecha] = useState(() => fechaPorDefecto(month));
  const [justificante, setJustificante] = useState<File | null>(file);

  const guardar = useMutation({
    mutationFn: () =>
      api.registerOffBankPayment(gap.rentChargeId ?? '', { method: metodo, amount: importe, paidAt: fecha }, justificante),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['rent-charges'] });
      toast.success(justificante ? 'Cobro registrado con su justificante.' : 'Cobro registrado. Puedes subir el justificante después.');
      if (result.reopened) avisarReapertura(month);
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido registrar el cobro.'),
  });

  const valido = Boolean(gap.rentChargeId && Number(importe) > 0 && fecha);

  return (
    <Drawer
      open
      title="Cobrado fuera del banco"
      subtitle={`${gap.concept} · ${propertyName}`}
      onClose={onClose}
      footer={
        <div className={styles.pie}>
          <span />
          <div className={styles.pieDerecha}>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={guardar.isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={() => guardar.mutate()} disabled={!valido || guardar.isPending}>
              {guardar.isPending ? 'Guardando…' : 'Registrar cobro'}
            </Button>
          </div>
        </div>
      }
    >
      <FormSection>
        <RadioGroup
          name="metodo"
          legend="Cómo se cobró"
          value={metodo}
          onChange={setMetodo}
          options={[
            { value: 'efectivo', title: 'En efectivo' },
            { value: 'bizum', title: 'Por Bizum' },
            { value: 'transferencia', title: 'Transferencia a otra cuenta' },
            { value: 'tarjeta', title: 'Con tarjeta' },
          ]}
        />
      </FormSection>
      <FormSection>
        <FormRow>
          <Field label="Importe" required>
            <MoneyInput value={importe} onValueChange={setImporte} onChange={() => undefined} />
          </Field>
          <Field label="Fecha" required>
            <Input type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} />
          </Field>
        </FormRow>
      </FormSection>
      <FormSection title="Justificante" hint="El recibo firmado, la captura del Bizum… Opcional, pero es lo que se descarga con el mes.">
        {justificante ? (
          <p className={styles.aviso}>
            {justificante.name}{' '}
            <button type="button" className={styles.statementUndo} onClick={() => setJustificante(null)}>
              Quitar
            </button>
          </p>
        ) : (
          <FileDrop label="Soltar justificante" onFile={setJustificante} />
        )}
      </FormSection>
    </Drawer>
  );
}
